/**
 * SupplierJourneyModel (AJIP-16.02.008 · WS3, WS4, WS7, WS8)
 * ---------------------------------------------------------------------
 * 12 stages, 16 events, deterministic transition table, stage→engine
 * state map. Mirrors Buyer Journey pattern for consistency but with
 * supplier-specific stages.
 *
 * Founder Rulings honoured:
 *   S3 — no `no-reentry-after:completed`. Multiple independent
 *        objectives are supported by the orchestrator via distinct
 *        instance identities (see SupplierJourneyOrchestrator).
 *   S4 — VERIFICATION_OUTCOME_RECEIVED is a passive record of an
 *        external outcome, not a manufacture of trust truth.
 *
 * P2-03 applied prospectively — every event name is `SUPPLIER_*`
 * prefixed and every internal observability event is `SUPPLIER_JOURNEY_*`
 * prefixed for stable namespacing across Journey packages.
 */
import { STATES } from '../core/JourneyState';

const SUPPLIER_STAGES = Object.freeze({
  ONBOARDING_INITIATED: 'ONBOARDING_INITIATED',
  PROFILE_DRAFTED: 'PROFILE_DRAFTED',
  CLAIM_SUBMITTED: 'CLAIM_SUBMITTED',
  CLAIM_REVIEW_PENDING: 'CLAIM_REVIEW_PENDING',
  CLAIM_APPROVED: 'CLAIM_APPROVED',
  VERIFICATION_INITIATED: 'VERIFICATION_INITIATED',
  VERIFICATION_EVIDENCE_SUBMITTED: 'VERIFICATION_EVIDENCE_SUBMITTED',
  VERIFICATION_OUTCOME_RECEIVED: 'VERIFICATION_OUTCOME_RECEIVED',
  RFQ_OPPORTUNITY_RECEIVED: 'RFQ_OPPORTUNITY_RECEIVED',
  QUOTE_DRAFTED: 'QUOTE_DRAFTED',
  QUOTE_SUBMITTED: 'QUOTE_SUBMITTED',
  OBJECTIVE_COMPLETED: 'OBJECTIVE_COMPLETED',
});
const ALL_SUPPLIER_STAGES = Object.freeze(Object.values(SUPPLIER_STAGES));

const SUPPLIER_EVENTS = Object.freeze({
  SUPPLIER_ONBOARDING_INITIATED: 'SUPPLIER_ONBOARDING_INITIATED',
  SUPPLIER_PROFILE_DRAFTED: 'SUPPLIER_PROFILE_DRAFTED',
  SUPPLIER_CLAIM_SUBMITTED: 'SUPPLIER_CLAIM_SUBMITTED',
  SUPPLIER_CLAIM_REVIEW_PENDING: 'SUPPLIER_CLAIM_REVIEW_PENDING',
  SUPPLIER_CLAIM_APPROVED: 'SUPPLIER_CLAIM_APPROVED',
  SUPPLIER_VERIFICATION_INITIATED: 'SUPPLIER_VERIFICATION_INITIATED',
  SUPPLIER_VERIFICATION_EVIDENCE_SUBMITTED: 'SUPPLIER_VERIFICATION_EVIDENCE_SUBMITTED',
  SUPPLIER_VERIFICATION_OUTCOME_RECEIVED: 'SUPPLIER_VERIFICATION_OUTCOME_RECEIVED',
  SUPPLIER_RFQ_OPPORTUNITY_RECEIVED: 'SUPPLIER_RFQ_OPPORTUNITY_RECEIVED',
  SUPPLIER_QUOTE_DRAFTED: 'SUPPLIER_QUOTE_DRAFTED',
  SUPPLIER_QUOTE_SUBMITTED: 'SUPPLIER_QUOTE_SUBMITTED',
  SUPPLIER_OBJECTIVE_COMPLETED: 'SUPPLIER_OBJECTIVE_COMPLETED',
  SUPPLIER_JOURNEY_BLOCKED: 'SUPPLIER_JOURNEY_BLOCKED',
  SUPPLIER_JOURNEY_CANCELLED: 'SUPPLIER_JOURNEY_CANCELLED',
  SUPPLIER_JOURNEY_FAILED: 'SUPPLIER_JOURNEY_FAILED',
  SUPPLIER_JOURNEY_RESUMED: 'SUPPLIER_JOURNEY_RESUMED',
});
const ALL_SUPPLIER_EVENTS = Object.freeze(Object.values(SUPPLIER_EVENTS));

const STAGE_TO_ENGINE_STATE = Object.freeze({
  ONBOARDING_INITIATED: STATES.STARTED,
  PROFILE_DRAFTED: STATES.IN_PROGRESS,
  CLAIM_SUBMITTED: STATES.IN_PROGRESS,
  CLAIM_REVIEW_PENDING: STATES.WAITING,
  CLAIM_APPROVED: STATES.IN_PROGRESS,
  VERIFICATION_INITIATED: STATES.IN_PROGRESS,
  VERIFICATION_EVIDENCE_SUBMITTED: STATES.WAITING,
  VERIFICATION_OUTCOME_RECEIVED: STATES.IN_PROGRESS,
  RFQ_OPPORTUNITY_RECEIVED: STATES.IN_PROGRESS,
  QUOTE_DRAFTED: STATES.IN_PROGRESS,
  QUOTE_SUBMITTED: STATES.IN_PROGRESS,
  OBJECTIVE_COMPLETED: STATES.COMPLETED,
});

// Stage transitions. `persist: true` marks authorised checkpoint per WS12.
// `engineTransition` maps the transition onto an engine-level state
// change (suspend/resume/complete). All other transitions are pure
// stage progressions handled via engine.updateStage().
const SUPPLIER_STAGE_TRANSITIONS = Object.freeze([
  { from: SUPPLIER_STAGES.ONBOARDING_INITIATED, event: SUPPLIER_EVENTS.SUPPLIER_PROFILE_DRAFTED, to: SUPPLIER_STAGES.PROFILE_DRAFTED, persist: true },
  { from: SUPPLIER_STAGES.PROFILE_DRAFTED, event: SUPPLIER_EVENTS.SUPPLIER_CLAIM_SUBMITTED, to: SUPPLIER_STAGES.CLAIM_SUBMITTED, persist: true },
  { from: SUPPLIER_STAGES.CLAIM_SUBMITTED, event: SUPPLIER_EVENTS.SUPPLIER_CLAIM_REVIEW_PENDING, to: SUPPLIER_STAGES.CLAIM_REVIEW_PENDING, persist: true, engineTransition: 'wait' },
  { from: SUPPLIER_STAGES.CLAIM_REVIEW_PENDING, event: SUPPLIER_EVENTS.SUPPLIER_CLAIM_APPROVED, to: SUPPLIER_STAGES.CLAIM_APPROVED, persist: true, engineTransition: 'resume' },
  { from: SUPPLIER_STAGES.CLAIM_APPROVED, event: SUPPLIER_EVENTS.SUPPLIER_VERIFICATION_INITIATED, to: SUPPLIER_STAGES.VERIFICATION_INITIATED, persist: true },
  { from: SUPPLIER_STAGES.VERIFICATION_INITIATED, event: SUPPLIER_EVENTS.SUPPLIER_VERIFICATION_EVIDENCE_SUBMITTED, to: SUPPLIER_STAGES.VERIFICATION_EVIDENCE_SUBMITTED, persist: true, engineTransition: 'wait' },
  // Founder Ruling S4 — the journey records only that an outcome was
  // received. It NEVER manufactures the outcome. Payload is opaque.
  { from: SUPPLIER_STAGES.VERIFICATION_EVIDENCE_SUBMITTED, event: SUPPLIER_EVENTS.SUPPLIER_VERIFICATION_OUTCOME_RECEIVED, to: SUPPLIER_STAGES.VERIFICATION_OUTCOME_RECEIVED, persist: true, engineTransition: 'resume' },
  { from: SUPPLIER_STAGES.VERIFICATION_OUTCOME_RECEIVED, event: SUPPLIER_EVENTS.SUPPLIER_RFQ_OPPORTUNITY_RECEIVED, to: SUPPLIER_STAGES.RFQ_OPPORTUNITY_RECEIVED, persist: true },
  { from: SUPPLIER_STAGES.RFQ_OPPORTUNITY_RECEIVED, event: SUPPLIER_EVENTS.SUPPLIER_QUOTE_DRAFTED, to: SUPPLIER_STAGES.QUOTE_DRAFTED, persist: true },
  { from: SUPPLIER_STAGES.QUOTE_DRAFTED, event: SUPPLIER_EVENTS.SUPPLIER_QUOTE_SUBMITTED, to: SUPPLIER_STAGES.QUOTE_SUBMITTED, persist: true },
  { from: SUPPLIER_STAGES.QUOTE_SUBMITTED, event: SUPPLIER_EVENTS.SUPPLIER_OBJECTIVE_COMPLETED, to: SUPPLIER_STAGES.OBJECTIVE_COMPLETED, persist: true, engineTransition: 'complete' },
]);

const findSupplierTransition = (fromStage, event) =>
  SUPPLIER_STAGE_TRANSITIONS.find((t) => t.from === fromStage && t.event === event) || null;

export {
  SUPPLIER_STAGES, ALL_SUPPLIER_STAGES,
  SUPPLIER_EVENTS, ALL_SUPPLIER_EVENTS,
  STAGE_TO_ENGINE_STATE, SUPPLIER_STAGE_TRANSITIONS,
  findSupplierTransition,
};
